var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { DocumentRetrievalFailureReason, DocumentRetrievalLocationStatus, DocumentVectorizationStatus, } from "healthex-common/document.retrieval.dtos";
import { PatientDataVisualizationPdfStatus } from "healthex-common/patient.data.visualization.dtos";
import { isErr, isOk } from "healthex-common/result";
import { asEnum } from "healthex-common/utils";
import { computed, observable, reaction } from "mobx";
import { getHeapGlobalEventProperties } from "../Infra/LoadHeap";
const DOCUMENT_RETRIEVAL_RECORDS_REUSED_EVENT = "DocumentRetrievalRecordsReused";
export class DocumentRetrievalManager {
    get retrieval() {
        return this._retrieval;
    }
    get patientId() {
        var _a;
        return (_a = this.authManager.id) !== null && _a !== void 0 ? _a : "";
    }
    constructor(documentRetrievalService, authManager, analyticsService) {
        this.documentRetrievalService = documentRetrievalService;
        this.authManager = authManager;
        this.analyticsService = analyticsService;
        this.pollingTimerId = null;
        this.pendingRetrievalKeys = new Set();
        this._retrieval = null;
        this.retrievalErrors = [];
        reaction(() => this.retrieval, () => {
            if (this.isRetrievalInProgress()) {
                this.startPollingTimerIfNeeded();
            }
            else {
                this.clearPollingTimerIfNeeded();
            }
        }, { fireImmediately: true });
    }
    async getExistingInProgressDocumentRetrieval() {
        const batch = await this.getExistingRetrieval(undefined, false);
        if (!batch)
            return null;
        await this.updateBatch(batch);
        return batch;
    }
    async initiateDocumentRetrieval(studyId, useTefca) {
        const retrievalKey = `${this.patientId}-${studyId !== null && studyId !== void 0 ? studyId : "null"}`;
        if (this.pendingRetrievalKeys.has(retrievalKey)) {
            return null;
        }
        this.pendingRetrievalKeys.add(retrievalKey);
        const batchResult = await this.documentRetrievalService.retrievePatientData(this.patientId, { studyId, useTefca });
        let batch = null;
        if (isErr(batchResult)) {
            this.pendingRetrievalKeys.delete(retrievalKey);
            const errorBatch = await this.getExistingInProgressDocumentRetrieval();
            batch = errorBatch;
        }
        else {
            batch = batchResult;
        }
        if (!batch)
            return null;
        const tefcaErrors = batch.tefcaQueries
            .filter((query) => query.error)
            .map((query) => {
            var _a, _b, _c;
            const errorStr = (_b = (_a = query.error) === null || _a === void 0 ? void 0 : _a.replace(/^ForbiddenException: /, "")) !== null && _b !== void 0 ? _b : "";
            return (_c = asEnum(DocumentRetrievalFailureReason)(errorStr)) !== null && _c !== void 0 ? _c : DocumentRetrievalFailureReason.GENERIC_ERROR;
        });
        if (tefcaErrors.length > 0) {
            this.setTEFCAErrors(tefcaErrors);
        }
        await this.updateBatch(batch);
        this.pendingRetrievalKeys.delete(retrievalKey);
        return batch.batchId;
    }
    async retryTEFCARetrieval(batchId) {
        const batch = await this.refreshBatch(batchId);
        if (!batch)
            return;
        this.clearTEFCAErrors();
        if (batch.tefcaQueries.some((query) => !query.isComplete)) {
            void this.startTEFCARetrieval(batch.batchId);
        }
    }
    async skipTEFCARetrieval(batchId) {
        const batch = await this.refreshBatch(batchId);
        if (!batch)
            return;
        this.clearTEFCAErrors();
        const updatedBatch = await this.documentRetrievalService.skipTEFCARetrieval(this.patientId, batchId);
        if (isOk(updatedBatch)) {
            await this.updateBatch(updatedBatch);
        }
    }
    async getMostRecentFinalizedTaskBatch(patientId, mustHaveRecords) {
        return await this.documentRetrievalService.getMostRecentFinalizedTaskBatch(patientId, mustHaveRecords);
    }
    async getDownloadPdfLink(batchId) {
        return await this.documentRetrievalService.getTaskBatchDownloadPdfLink(this.patientId, batchId);
    }
    startPollingTimerIfNeeded() {
        this.clearPollingTimerIfNeeded();
        this.pollingTimerId = window.setInterval(() => {
            void this.refreshInProgressBatch();
        }, 1000);
    }
    clearPollingTimerIfNeeded() {
        if (this.pollingTimerId !== null) {
            window.clearInterval(this.pollingTimerId);
            this.pollingTimerId = null;
        }
    }
    clearPollingTimerIfStartedByFastenCallback() {
        this.clearPollingTimerIfNeeded();
    }
    isRetrievalInProgress() {
        const retrieval = this.retrieval;
        if (!retrieval)
            return false;
        if (retrieval.tefcaQueries.some((query) => !query.isComplete)) {
            return true;
        }
        if (!retrieval.documentSelectionComplete) {
            if (retrieval.locations.some((location) => location.status === DocumentRetrievalLocationStatus.IN_PROGRESS)) {
                return true;
            }
            return false;
        }
        if (retrieval.isAIFlow &&
            (retrieval.documentVectorizationStatus === DocumentVectorizationStatus.NOT_STARTED ||
                retrieval.documentVectorizationStatus === DocumentVectorizationStatus.IN_PROGRESS ||
                retrieval.documentVectorizationStatus === DocumentVectorizationStatus.PENDING)) {
            return true;
        }
        const retrievalHasDocuments = retrieval.documents.length > 0;
        const pdfIsCompleted = retrieval.pdfStatus === PatientDataVisualizationPdfStatus.COMPLETED ||
            retrieval.pdfStatus === PatientDataVisualizationPdfStatus.ERROR;
        if (!retrieval.finalized || (retrievalHasDocuments && !pdfIsCompleted)) {
            return true;
        }
        return false;
    }
    async refreshBatch(batchId) {
        if (!batchId) {
            console.warn(`Batch id is not valid`);
            return null;
        }
        const batch = await this.documentRetrievalService.getTaskBatch(this.patientId, batchId);
        if (!batch) {
            console.warn(`Batch ${batchId} not found`);
            return null;
        }
        await this.updateBatch(batch);
        return batch;
    }
    async refreshInProgressBatch() {
        if (!this.retrieval)
            return;
        if (!this.isRetrievalInProgress())
            return;
        await this.refreshBatch(this.retrieval.batchId);
    }
    async getExistingRetrieval(batchId, documentSelectionComplete) {
        return await this.documentRetrievalService.getExistingRetrieval(this.patientId, {
            batchId,
            documentSelectionComplete,
        });
    }
    async updateBatch(batch) {
        var _a;
        if (!batch)
            return;
        if (((_a = this._retrieval) === null || _a === void 0 ? void 0 : _a.batchId) !== batch.batchId) {
            console.log(`[Document Retrieval] New retrieval batch id: ${batch.batchId}`);
            this.clearTEFCAErrors();
        }
        this._retrieval = batch;
    }
    async startTEFCARetrieval(batchId) {
        const batch = await this.documentRetrievalService.startTEFCARetrieval(this.patientId, batchId);
        if (isErr(batch)) {
            this.setTEFCAErrors([batch.error]);
        }
        else {
            await this.updateBatch(batch);
        }
    }
    setTEFCAErrors(errors) {
        this.retrievalErrors = errors;
    }
    clearTEFCAErrors() {
        this.retrievalErrors = [];
    }
    async getFacilitatedFHIRAuthUrl(batchId, fhirEndpoint, redirectUrl, authType) {
        return await this.documentRetrievalService.getFacilitatedFHIRAuthUrl(this.patientId, batchId, fhirEndpoint, redirectUrl, authType);
    }
    async getFacilitatedFHIRRecords(batchId, locationId, fhirEndpoint, authType, redirectUri, code) {
        const batch = await this.documentRetrievalService.getFacilitatedFHIRRecords(this.patientId, batchId, locationId, fhirEndpoint, authType, redirectUri, code);
        await this.updateBatch(batch);
    }
    async getFastenDocuments(batchId, connectionId, portalId, brandId, endpointId) {
        const batch = await this.documentRetrievalService.trackFastenRetrieval(this.patientId, batchId, connectionId, portalId, brandId, endpointId);
        await this.updateBatch(batch);
    }
    async skipLocation(batchId, locationId) {
        const batch = await this.documentRetrievalService.updateTaskBatch(this.patientId, batchId, {
            skipLocationId: locationId,
        });
        await this.updateBatch(batch);
    }
    async updateExcludedLocations(batchId, excludedLocationIds) {
        const batch = await this.documentRetrievalService.updateTaskBatch(this.patientId, batchId, {
            excludedLocationIds,
        });
        await this.updateBatch(batch);
    }
    async doneSelectingDocuments(batchId, studyId, createdBatch) {
        await this.documentRetrievalService.createDocumentDelivery(this.patientId, {
            batchId,
            studyId,
            createdBatch,
        });
        const batch = await this.documentRetrievalService.updateTaskBatch(this.patientId, batchId, {
            documentSelectionComplete: true,
        });
        await this.updateBatch(batch);
    }
    async reuseExistingDocuments(existingBatchId, studyId) {
        await this.documentRetrievalService.createDocumentDelivery(this.patientId, {
            batchId: existingBatchId,
            studyId,
            createdBatch: null,
        });
        void this.analyticsService.trackHeapEvent({
            event: DOCUMENT_RETRIEVAL_RECORDS_REUSED_EVENT,
            identity: this.patientId,
            properties: {
                ...getHeapGlobalEventProperties(),
            },
        });
    }
    async downloadInitiated(batchId) {
        const batch = await this.documentRetrievalService.updateTaskBatch(this.patientId, batchId, {
            patientHasDownloaded: true,
        });
        await this.updateBatch(batch);
    }
    async getDownloadDocumentsLink(batchId) {
        return await this.documentRetrievalService.getTaskBatchDownloadLink(this.patientId, batchId);
    }
    async getMostRecentDownloadableOrProcessingTaskBatch() {
        return await this.documentRetrievalService.getMostRecentDownloadableOrProcessingTaskBatch(this.patientId);
    }
    async getPatientTEFCAAuthInfo() {
        return await this.documentRetrievalService.getPatientTEFCAAuthInfo(this.patientId);
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], DocumentRetrievalManager.prototype, "_retrieval", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalManager.prototype, "retrieval", null);
__decorate([
    observable,
    __metadata("design:type", Array)
], DocumentRetrievalManager.prototype, "retrievalErrors", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], DocumentRetrievalManager.prototype, "patientId", null);
